import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import { Badge, Field, PageTitle, btnPrimary, card, inputCls, linkCls } from "@/components/admin/ui";

export default function AdminSettings() {
  const { data: settings, setData } = useApi("/admin/settings");
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (settings && !form) setForm({ publish_event_url: settings.publish_event_url || "", suggest_feature_url: settings.suggest_feature_url || "" });
  }, [settings, form]);

  if (!form) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setData(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin)
    return (
      <div data-testid="admin-settings-page">
        <PageTitle title="Settings" />
        <div className={`${card} p-5 text-[13px]`} data-testid="organizer-account-card">
          <p className="mb-1 text-[15px] font-semibold">Your account</p>
          <p className="text-[#6B7280]">
            Signed in as <span className="text-[#111]" data-testid="settings-admin-email">{user?.email}</span>
            {user?.organizer_for ? <> · Event organizer for <span className="text-[#111]">{user.organizer_for}</span></> : null}
          </p>
          <p className="mt-3 text-[#6B7280]">
            Bank details and payouts live under <Link to="/admin/payouts" className={linkCls}>Payouts</Link>. Need your name, password or festivals changed? Ask the Dancerfloor team.
          </p>
        </div>
      </div>
    );

  return (
    <div data-testid="admin-settings-page">
      <PageTitle title="Settings">
        <button type="button" className={btnPrimary} onClick={save} disabled={saving} data-testid="save-settings-button">{saving ? "Saving…" : "Save changes"}</button>
      </PageTitle>
      <div className="space-y-5">
        <div className={`${card} p-5 space-y-5`}>
          <p className="text-[15px] font-semibold">Avatar menu links</p>
          <Field label="“Publish your event” link"><input value={form.publish_event_url} onChange={(e) => setForm({ ...form, publish_event_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="publish-event-url-input" /></Field>
          <Field label="“Suggest a feature” link"><input value={form.suggest_feature_url} onChange={(e) => setForm({ ...form, suggest_feature_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="suggest-feature-url-input" /></Field>
        </div>
        <div className={`${card} p-5 space-y-3 text-[13px]`} data-testid="integrations-panel">
          <p className="text-[15px] font-semibold">Integrations</p>
          <div className="flex items-center justify-between"><span>Video hosting</span><Badge tone="green">connected</Badge></div>
          <div className="flex items-center justify-between">
            <span>Image hosting</span>
            <Badge tone={settings?.storage_configured ? "green" : "yellow"}>{settings?.storage_configured ? "connected" : "local fallback"}</Badge>
          </div>
          {!settings?.storage_configured && <p className="text-[12px] text-[#6B7280]">Images are stored on the app server until image hosting is added to the server configuration.</p>}
          <div className="flex items-center justify-between"><span>Stripe (payments)</span><Badge tone={settings?.stripe_configured ? "green" : "gray"}>{settings?.stripe_configured ? `connected · ${settings.stripe_mode} mode` : "not connected"}</Badge></div>
          <p className="text-[12px] text-[#6B7280]">"Purchase Pass" opens Stripe Checkout with the tier's price. A pass with an external checkout link uses that link instead. Purchases are listed under Sales.</p>
        </div>
        <div className={`${card} p-5 text-[13px]`}>
          <p className="text-[15px] font-semibold mb-1">Admin account</p>
          <p className="text-[#6B7280]">Signed in as <span className="text-[#111]" data-testid="settings-admin-email">{user?.email}</span>. Password changes are made in the server configuration.</p>
        </div>
      </div>
    </div>
  );
}
